import { useQuery } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { useMemo } from 'react';

import { GET_AUDIT_SESSION } from '../graphql/queries/getAuditSession';

interface GetAuditSessionResponse {
  community: {
    id: string;
    name: string;
    communityGroupId: string;
    logoUrl?: string;
    optionalMetadata?: {
      description?: string;
      pgRating?: string;
      contentGenres?: string[];
      contentStandardsUrl?: string;
      bigId?: string;
    };
    auditSession: {
      id: string;
      name: string;
      description?: string;
      status: string;
      createdBy: string;
      createdAt: string;
      completedAt?: string;
      lastModifiedBy?: string;
      lastModifiedAt?: string;
      itemsCount: number;
      completedCount: number;
      skippedCount: number;
      pendingCount: number;
      sampleParameters?: {
        communityId: string;
        samplingParameters: {
          confidenceLevel: number;
          marginOfError: number;
          populationProportion: number;
          timeframe: {
            start: string;
            end: string;
          };
        };
        filters: {
          languages?: string[];
          topics?: string[];
          labels?: string[];
          labelSeverities?: number[];
        };
      };
      sampleStatistics?: {
        sampleSize: number;
        confidenceLevel: number;
        marginOfError: number;
        populationSize: number;
      };
      datasetComposition?: {
        byTopic: Array<{
          key: string;
          count: number;
          percentage: number;
        }>;
        byLanguage: Array<{
          key: string;
          count: number;
          percentage: number;
        }>;
        byLabel: Array<{
          key: string;
          count: number;
          percentage: number;
        }>;
        bySeverity: Array<{
          key: string;
          count: number;
          percentage: number;
        }>;
        totalItems: number;
      };
    };
  };
}

/**
 * Hook for fetching audit session metadata and community data needed for the header
 * Part of the 4-query architecture - fetches only session data, not items or decisions
 */
export function useGetAuditSession(communityId: string | undefined, sessionId: string | undefined) {
  const { data, loading, error, refetch } = useQuery<GetAuditSessionResponse>(GET_AUDIT_SESSION, {
    variables: { communityId, sessionId },
    skip: !communityId || !sessionId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network', // Instant cache + fresh network data on first load
    nextFetchPolicy: 'cache-first', // Subsequent queries use cache-first
    notifyOnNetworkStatusChange: false, // Don't show loading during background refetches
  });

  // Extract session and community data
  const result = useMemo(() => {
    if (!data?.community?.auditSession) {
      return { session: null, community: null };
    }

    return {
      session: data.community.auditSession,
      community: data.community,
    };
  }, [data]);

  // Transform error using adapter pattern
  const domainError = useMemo(() => {
    if (!error) return undefined;

    const mappedError = transformError(error);

    return {
      ...mappedError,
      onRetry: () => refetch(),
    };
  }, [error, refetch]);

  return {
    session: result?.session || null,
    community: result?.community || null,
    loading,
    error: domainError,
    refetch,
  };
}
